function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { FormattedMessage } from 'react-intl';
import useStyles from 'isomorphic-style-loader/useStyles';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import filter from 'lodash/filter';
import Account from 'modules/Account';
import App from 'modules/App';
import { useAction, useProgressSuccess } from 'hooks';
import * as actions from '../../actions';
import { DEFAULT_NOTIFICATION_FILTER } from '../../constants';
import * as selectors from '../../selectors';
import messages from '../../messages';
import styles from './NotificationsPopover.pcss';
import NotificationsList from './NotificationsList';
import NotificationsFilters from './NotificationFilters';
import ClearFiltersButton from './ClearFiltersButton';
import FiltersButton from './FiltersButton';
import SettingsButton from './SettingsButton';
const NotificationsPopover = ({ notificationsPopoverRef, isOpen })=>{
    useStyles(styles);
    const activeOrganizationUID = useSelector(Account.selectors.activeOrganizationUID);
    const activeHcpClinicMemberships = useSelector(Account.selectors.activeHcpClinicMemberships);
    const notificationsFilter = useSelector(selectors.notificationsFilter);
    const notificationsTotalActive = useSelector(selectors.notificationsTotalActive);
    const notificationsRecords = useSelector(selectors.notificationsRecords);
    const createSignalRHub = useAction(App.actions.createSignalRHub);
    const fetchNotificationsSettings = useAction(Account.actions.fetchNotificationsSettings);
    const setClinicMembershipHasActiveNotifications = useAction(Account.actions.setClinicMembershipHasActiveNotifications);
    const fetchList = useAction(actions.fetchList);
    const clearFilter = useAction(actions.setNotificationFilterParams, DEFAULT_NOTIFICATION_FILTER);
    const markNotificationsRead = useAction(actions.markNotificationsRead);
    const [view, setView] = useState('List');
    const toggleView = useCallback(debounce(()=>{
        setView((prev)=>prev === 'List' ? 'Filters' : 'List');
    }, 50), []);
    const popoverClosed = ()=>{
        const activeNotifications = filter(notificationsRecords, {
            status: 'Active'
        });
        const activeIds = map(activeNotifications, (n)=>n.instantNotificationId);
        if (activeIds.length) {
            markNotificationsRead();
        }
    };
    useEffect(()=>{
        createSignalRHub();
    }, []);
    useEffect(()=>{
        if (!isOpen && view !== 'List') {
            setView('List');
        }
    }, [
        isOpen
    ]);
    useEffect(()=>{
        fetchList();
    }, [
        notificationsFilter,
        activeOrganizationUID
    ]);
    useEffect(()=>{
        if (activeOrganizationUID) {
            fetchNotificationsSettings();
        }
        const isClean = isEqual(notificationsFilter, DEFAULT_NOTIFICATION_FILTER);
        if (!isClean) {
            clearFilter();
        }
    }, [
        activeOrganizationUID
    ]);
    useEffect(()=>{
        if (activeHcpClinicMemberships) {
            const { hasActiveNotifications, clinicHcpMembershipId } = activeHcpClinicMemberships;
            const currentHasActiveNotifications = notificationsTotalActive > 0;
            if (hasActiveNotifications !== currentHasActiveNotifications) {
                setClinicMembershipHasActiveNotifications(clinicHcpMembershipId, currentHasActiveNotifications);
            }
        }
    }, [
        notificationsTotalActive
    ]);
    useProgressSuccess(isOpen, false, popoverClosed);
    if (!isOpen) return null;
    return /*#__PURE__*/ _jsx(AnimatePresence, {
        children: /*#__PURE__*/ _jsxs(motion.div, {
            className: styles.notifications,
            role: "presentation",
            ref: notificationsPopoverRef,
            initial: {
                opacity: 0
            },
            animate: {
                opacity: 1
            },
            exit: {
                opacity: 0
            },
            transition: {
                ease: 'easeOut',
                duration: 0.15
            },
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: styles.notifications__header,
                    "data-testid": "notifications-popover-header",
                    children: [
                        /*#__PURE__*/ _jsx("h2", {
                            className: "text--h2",
                            children: /*#__PURE__*/ _jsx(FormattedMessage, _object_spread({}, messages.headers.notifications))
                        }),
                        /*#__PURE__*/ _jsx(SettingsButton, {}),
                        /*#__PURE__*/ _jsxs("div", {
                            className: styles.notifications__filtersPanel,
                            children: [
                                /*#__PURE__*/ _jsx(FiltersButton, {
                                    onClick: toggleView,
                                    isActive: view === 'Filters'
                                }),
                                /*#__PURE__*/ _jsx(ClearFiltersButton, {
                                    currentView: view
                                })
                            ]
                        })
                    ]
                }),
                view === 'List' && /*#__PURE__*/ _jsx(NotificationsList, {}),
                view === 'Filters' && /*#__PURE__*/ _jsx(NotificationsFilters, {
                    toggleView: toggleView
                })
            ]
        })
    });
};
export default NotificationsPopover;
